import { useEffect, useState } from 'react';
import { saveGridState, loadGridState, GRID_PERSIST_KEYS } from '../utils/LocalStorageHelper';

const DEFAULT_PERSIST_GRID_STATE = {
  pageSize: false,
  density: false,
  columnVisibility: false,
  columnWidths: false,
  sortModel: false,
  searchText: false,
  columnSearch: false,
  filters: false,
  pinnedColumns: false,
};

const UseGridState = ({
  storageKey,
  persistGridState = DEFAULT_PERSIST_GRID_STATE,

  defaultPageSize = 10,
  defaultDensity = 'compact',
}) => {
  const canPersist = (persistGridState, key) => {
    if (typeof persistGridState === 'boolean') {
      return persistGridState;
    }

    return persistGridState?.[key] ?? true;
  };

  // Page Size
  const [pageSize, setPageSize] = useState(() =>
    canPersist(persistGridState, GRID_PERSIST_KEYS.PAGE_SIZE)
      ? loadGridState(storageKey, GRID_PERSIST_KEYS.PAGE_SIZE, defaultPageSize)
      : defaultPageSize
  );

  // Density
  const [density, setDensity] = useState(() =>
    canPersist(persistGridState, GRID_PERSIST_KEYS.DENSITY)
      ? loadGridState(storageKey, GRID_PERSIST_KEYS.DENSITY, defaultDensity)
      : defaultDensity
  );

  // Column Visibility
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() =>
    canPersist(persistGridState, GRID_PERSIST_KEYS.COLUMN_VISIBILITY)
      ? loadGridState(storageKey, GRID_PERSIST_KEYS.COLUMN_VISIBILITY, {})
      : {}
  );

  //Column Width
  const [columnWidths, setColumnWidths] = useState(() =>
    canPersist(persistGridState, GRID_PERSIST_KEYS.COLUMN_WIDTHS)
      ? loadGridState(storageKey, GRID_PERSIST_KEYS.COLUMN_WIDTHS, {})
      : {}
  );

  // Sort Model
  const [sortModel, setSortModel] = useState(() =>
    canPersist(persistGridState, GRID_PERSIST_KEYS.SORT_MODEL)
      ? loadGridState(storageKey, GRID_PERSIST_KEYS.SORT_MODEL, [])
      : []
  );

  // Search
  const [searchText, setSearchText] = useState(() =>
    canPersist(persistGridState, GRID_PERSIST_KEYS.SEARCH_TEXT)
      ? loadGridState(storageKey, GRID_PERSIST_KEYS.SEARCH_TEXT, '')
      : ''
  );

  // Column Search
  const [columnSearch, setColumnSearch] = useState(() =>
    canPersist(persistGridState, GRID_PERSIST_KEYS.COLUMN_SEARCH)
      ? loadGridState(storageKey, GRID_PERSIST_KEYS.COLUMN_SEARCH, {})
      : {}
  );

  // Filters
  const [filters, setFilters] = useState(() =>
    canPersist(persistGridState, GRID_PERSIST_KEYS.FILTERS)
      ? loadGridState(storageKey, GRID_PERSIST_KEYS.FILTERS, [])
      : []
  );

  // Pinned Columns
  const [pinnedColumns, setPinnedColumns] = useState(() =>
    canPersist(persistGridState, GRID_PERSIST_KEYS.PINNED_COLUMNS)
      ? loadGridState(storageKey, GRID_PERSIST_KEYS.PINNED_COLUMNS, {
          left: [],
          right: [],
        })
      : {
          left: [],
          right: [],
        }
  );
  // Column Order
  const [columnOrder, setColumnOrder] = useState(() =>
    canPersist(persistGridState, GRID_PERSIST_KEYS.COLUMN_ORDER)
      ? loadGridState(storageKey, GRID_PERSIST_KEYS.COLUMN_ORDER, [])
      : []
  );

  //Save Page Size
  useEffect(() => {
    if (storageKey && canPersist(persistGridState, GRID_PERSIST_KEYS.PAGE_SIZE)) {
      saveGridState(storageKey, GRID_PERSIST_KEYS.PAGE_SIZE, pageSize);
    }
  }, [pageSize, storageKey, persistGridState]);

  //Save Density
  useEffect(() => {
    if (storageKey && canPersist(persistGridState, GRID_PERSIST_KEYS.DENSITY)) {
      saveGridState(storageKey, GRID_PERSIST_KEYS.DENSITY, density);
    }
  }, [density, storageKey, persistGridState]);

  //Save Column Visibility
  useEffect(() => {
    if (storageKey && canPersist(persistGridState, GRID_PERSIST_KEYS.COLUMN_VISIBILITY)) {
      saveGridState(storageKey, GRID_PERSIST_KEYS.COLUMN_VISIBILITY, columnVisibilityModel);
    }
  }, [columnVisibilityModel, storageKey, persistGridState]);

  //Column Width
  useEffect(() => {
    if (storageKey && canPersist(persistGridState, GRID_PERSIST_KEYS.COLUMN_WIDTHS)) {
      saveGridState(storageKey, GRID_PERSIST_KEYS.COLUMN_WIDTHS, columnWidths);
    }
  }, [columnWidths, storageKey, persistGridState]);

  // Save Sorting
  useEffect(() => {
    if (storageKey && canPersist(persistGridState, GRID_PERSIST_KEYS.SORT_MODEL)) {
      saveGridState(storageKey, GRID_PERSIST_KEYS.SORT_MODEL, sortModel);
    }
  }, [sortModel, storageKey, persistGridState]);

  // Save Search
  useEffect(() => {
    if (storageKey && canPersist(persistGridState, GRID_PERSIST_KEYS.SEARCH_TEXT)) {
      saveGridState(storageKey, GRID_PERSIST_KEYS.SEARCH_TEXT, searchText);
    }
  }, [searchText, storageKey, persistGridState]);

  //Save Column Search
  useEffect(() => {
    if (storageKey && canPersist(persistGridState, GRID_PERSIST_KEYS.COLUMN_SEARCH)) {
      saveGridState(storageKey, GRID_PERSIST_KEYS.COLUMN_SEARCH, columnSearch);
    }
  }, [columnSearch, storageKey, persistGridState]);

  // Save Filters
  useEffect(() => {
    if (storageKey && canPersist(persistGridState, GRID_PERSIST_KEYS.FILTERS)) {
      saveGridState(storageKey, GRID_PERSIST_KEYS.FILTERS, filters);
    }
  }, [filters, storageKey, persistGridState]);

  //Save Pinned Columns
  useEffect(() => {
    if (storageKey && canPersist(persistGridState, GRID_PERSIST_KEYS.PINNED_COLUMNS)) {
      saveGridState(storageKey, GRID_PERSIST_KEYS.PINNED_COLUMNS, pinnedColumns);
    }
  }, [pinnedColumns, storageKey, persistGridState]);

  //Save Column Order
  useEffect(() => {
    if (storageKey && canPersist(persistGridState, GRID_PERSIST_KEYS.COLUMN_ORDER)) {
      saveGridState(storageKey, GRID_PERSIST_KEYS.COLUMN_ORDER, columnOrder);
    }
  }, [columnOrder, storageKey, persistGridState]);

  return {
    // Page Size
    pageSize,
    setPageSize,

    // Density
    density,
    setDensity,

    // Columns
    columnVisibilityModel,
    setColumnVisibilityModel,

    //Column Width
    columnWidths,
    setColumnWidths,

    // Column Search
    columnSearch,
    setColumnSearch,

    // Sorting
    sortModel,
    setSortModel,

    // Search
    searchText,
    setSearchText,

    // Filters
    filters,
    setFilters,

    // Pinned Columns
    pinnedColumns,
    setPinnedColumns,
    
    columnOrder,
    setColumnOrder,
  };
};

export default UseGridState;
